/**
 * planOptimizer — fill a plan's open slots for maximum projected value inside
 * the remaining budget.
 *
 * 🔴 IT LIVES IN utils/, NOT engine/. `src/engine/` is the auction bid AI, and
 * the batch-sim calibration depends on every byte of it. This is a planning
 * helper with no bid-path involvement whatsoever, and keeping it out of engine/
 * is what keeps "Draft Plan touches no engine path" a true statement rather
 * than one somebody has to re-verify.
 *
 * THE PROBLEM is a multiple-choice knapsack: choose one player per open slot,
 * respecting position eligibility, maximising Σ value subject to Σ price ≤
 * budget. NP-hard in general — but this instance is ~340 players over ≤16 slots
 * with a ≤$200 integer budget, which is small and structured enough to solve
 * EXACTLY, so it is solved exactly.
 *
 * 🔴 NOT GREEDY, AND THAT IS THE POINT. A value-per-dollar sort is provably
 * wrong on knapsack — planOptimizer.test.js contains an instance where the
 * highest-ratio player is NOT in the optimal answer, precisely so that a future
 * "simplification" to a greedy sort fails loudly. The whole value of this
 * feature is that the number can be trusted.
 *
 * 🔴 STARTERS EXACT, BENCH GREEDY — a deliberate asymmetry. Bench players
 * contribute nothing to a starters objective, so an exact solver would spend
 * every dollar on starters and leave the bench unfillable. So: reserve $1 per
 * open bench slot, solve the starters exactly against the real objective, then
 * fill the bench greedily from what is left. "Solving" a bench exactly against
 * a zero objective would be arbitrary, not optimal, and calling it optimal
 * would be a lie.
 *
 * THE SOLVE, two stages:
 *
 *   Stage 1, per position: best[P][k][c] = max value using exactly k distinct
 *   players of position P at total cost ≤ c. A 0/1 knapsack with a count
 *   dimension. RB is the worst case at roughly 121 players × a few counts ×
 *   ~200 costs — a few hundred thousand updates.
 *
 *   Stage 2, across positions: a DP over (cost used, flex slots consumed).
 *   Each position takes k = its own open slots plus whatever flex slots it
 *   claims. FLEX/SFLEX eligibility comes from rosterSlotFill, not a local list.
 *
 * Both stages keep enough state to reconstruct WHICH players, not just the
 * score.
 *
 * WHY NO cummax PASS: both tables are monotone non-decreasing in cost by
 * construction — best[P][k][·] because it is built from a constant row, and the
 * stage-2 table because any combination costing c can be replaced by the same
 * combination read at c+1, which is never worse. That is worth knowing before
 * anyone "fixes" a missing prefix-max.
 */

import { primaryOf, isLockedTarget } from "../hooks/usePlanState";
import { FLEX_ELIGIBLE, SFLEX_ELIGIBLE } from "./rosterSlotFill";

const NEG = -Infinity;

/** Positions the solver reasons about. BN is a slot kind, not a position. */
export const SOLVER_POSITIONS = ["QB", "RB", "WR", "TE", "K", "DEF"];

/**
 * How many of a position you may roster BEYOND the slots that can start it.
 *
 * 🔴 WHY THIS EXISTS. Neither objective means anything for a bench slot, so
 * without a cap the solver fills the bench with whatever scores highest per
 * dollar in the abstract: under PPG that is six $1 backup quarterbacks in a
 * 1QB league, under VORP it is four kickers. Both are useless rosters, and
 * both are the objective's fault rather than the solver's.
 *
 * The cap is a CONSTRAINT, not a patch on the greedy bench fill. It bounds the
 * k dimension of the stage-1 knapsack and the choices in stage 2 as well as the
 * bench, because the same pathology is reachable through the starters path in
 * unusual league shapes — a 3-flex format would happily start three TEs.
 *
 * K and DEF are 0 because nobody has ever needed a second kicker: you stream
 * them, and a backup occupies a bench slot that a flex-eligible body should
 * have. QB and TE get exactly one, which is the real-world handcuff.
 *
 * 🔴 RB AND WR ARE UNCAPPED ON PURPOSE, and that is what makes the caps safe:
 * both are FLEX-eligible and deep, so there is always a legal body available.
 * A cap can never render a fillable plan infeasible.
 */
export const BENCH_BONUS = { QB: 1, TE: 1, K: 0, DEF: 0, RB: Infinity, WR: Infinity };

/**
 * The most of position P this roster may hold in total — starters and bench.
 *
 * "Slots that can start P" is P's own slots plus any FLEX/SFLEX slot P is
 * eligible for, counted across the WHOLE roster rather than only the open part.
 * Superflex therefore derives with no special case: its SFLEX slot makes QB
 * startable twice, so cap(QB) rises to 3 on its own.
 */
export function positionCap(P, rosterSlots) {
  let startable = 0;
  for (const s of rosterSlots) {
    if (s.pos === P) startable += 1;
    else if (s.pos === "FLEX" && FLEX_ELIGIBLE.includes(P)) startable += 1;
    else if (s.pos === "SFLEX" && SFLEX_ELIGIBLE.includes(P)) startable += 1;
  }
  const bonus = BENCH_BONUS[P];
  return startable + (bonus === undefined ? 0 : bonus);
}

const keyOf = (p) => `${p.name}-${p.pos}`;

/** How a capped slot is named back to the user. Label, else the id. */
const slotLabel = (slot) => (slot && (slot.label || slot.id)) || "That spot";
const money = (n) => (n === Infinity ? "no cap" : `$${n}`);
/** ["RB","WR","TE"] reads back as "RB, WR or TE". */
const orList = (arr) => (arr.length <= 1
  ? (arr[0] || "")
  : `${arr.slice(0, -1).join(", ")} or ${arr[arr.length - 1]}`);

/**
 * The same plan reduced to its LOCKED slots only — the shape every mode that
 * "solves from scratch" is handed instead of `{ buckets: {} }`.
 *
 * 🔴 A LOCK IS NOT A FOURTH SOLVER MODE, AND THAT IS THE POINT OF THIS
 * FUNCTION. optimizePlan already knows how to hold a slot: a bucket with a
 * primary is skipped, its price comes off the budget first, and its player is
 * struck from the candidate pool. Locks reuse that path exactly — they are a
 * PER-SLOT keepCommitments that also survives `fromScratch`, which is the one
 * thing the existing whole-plan flag cannot express. So there is no second
 * held-slot concept to keep in step with the first, and every guarantee the
 * committed path already has (no double-offer, budget netted, $1 per remaining
 * empty slot reserved over the REDUCED slot set) applies to locks for free.
 *
 * 🔴 NOT the same idea as `isCommitted`, which stays where it is. That is a
 * budget predicate over SLOTS — "is this spot spoken for", true of a bare
 * earmark with nobody in it — and every locked slot is committed while most
 * committed slots are not locked. They are not collapsible.
 *
 * The bucket is rebuilt with the locked primary in cell 0 rather than passed
 * through: the backups are alternatives for a slot that is no longer open, and
 * handing them to the solver would only invite a future reader to think they
 * mean something here.
 */
export function lockedPlanOf(plan) {
  const buckets = {};
  Object.entries((plan && plan.buckets) || {}).forEach(([slotId, bucket]) => {
    const primary = primaryOf(bucket);
    // 🔴 THE RAIL BUDGET SURVIVES EVEN WHEN THE TARGETS DO NOT. Under
    // budgetMode "slot" a bucket's `budget` is a CONSTRAINT, not decoration, so
    // a from-scratch rank that dropped it would solve B-D under different rules
    // than A and quietly hand back columns that break the caps. Only the cells
    // are cleared for an unlocked slot; the earmark is the user's, not the
    // solver's, and it is not a target.
    if (isLockedTarget(primary)) {
      buckets[slotId] = { budget: bucket ? bucket.budget : null, cells: [primary, null, null, null] };
    } else if (bucket && bucket.budget != null) {
      buckets[slotId] = { budget: bucket.budget, cells: [null, null, null, null] };
    }
  });
  return { buckets, budget: plan ? plan.budget : 0 };
}

/** Every slot id this plan has locked, in rosterSlots order when given one. */
export function lockedSlotIds(plan, rosterSlots) {
  const buckets = (plan && plan.buckets) || {};
  const ids = rosterSlots ? rosterSlots.map((s) => s.id) : Object.keys(buckets);
  return ids.filter((id) => isLockedTarget(primaryOf(buckets[id])));
}

/** What the locked slots already cost, at the prices they were locked at. */
export function lockedSpendOf(plan) {
  let total = 0;
  Object.values((plan && plan.buckets) || {}).forEach((bucket) => {
    const primary = primaryOf(bucket);
    if (isLockedTarget(primary)) total += Number(primary.price) || 0;
  });
  return total;
}

/**
 * 0/1 knapsack with an exact-count dimension.
 *
 * Returns `best` — a flat (maxK+1)×(maxC+1) table where best[k][c] is the most
 * value obtainable from exactly k distinct players at total cost ≤ c — and
 * `used`, a per-step bitmap that makes reconstruction exact.
 *
 * `used[i][k][c]` records that taking player i STRICTLY improved state (k,c) at
 * step i. Walking i downwards from n and consuming a player whenever the bit is
 * set replays the optimum exactly; the naive alternative (storing "last player
 * to write this cell") is wrong, because a later player can overwrite a pointer
 * that an earlier state still depends on.
 */
function countKnapsack(prices, values, maxK, maxC, capsAsc) {
  const n = prices.length;
  const row = maxC + 1;
  const size = (maxK + 1) * row;

  let prev = new Float64Array(size).fill(NEG);
  prev.fill(0, 0, row);               // k = 0 costs nothing and is worth nothing
  let cur = new Float64Array(size);
  const used = new Uint8Array((n + 1) * size);

  for (let i = 1; i <= n; i++) {
    const p = prices[i - 1];
    const v = values[i - 1];
    cur.set(prev);
    const stepBase = i * size;
    for (let k = 1; k <= maxK; k++) {
      const base = k * row;
      const prevBase = (k - 1) * row;
      // 🔴 THE PER-SLOT CAP, AND IT IS ONE COMPARISON BECAUSE OF THE SORT.
      // In slot mode `prices` arrives ascending, so the player whose take moves
      // the count k-1 -> k is by construction the k-th CHEAPEST of the chosen
      // set. A multiset is assignable to k capped slots exactly when its
      // ascending prices sit under the slots' ascending caps pairwise, so
      // testing p against capsAsc[k] at the moment of the take enforces the
      // whole matching condition with no extra DP dimension.
      //
      // capsAsc is undefined in TOTAL mode and this branch never runs, which is
      // what keeps that path identical to what it was.
      if (capsAsc && p > capsAsc[k]) continue;
      for (let c = p; c <= maxC; c++) {
        const from = prev[prevBase + c - p];
        if (from === NEG) continue;
        const cand = from + v;
        if (cand > cur[base + c]) {
          cur[base + c] = cand;
          used[stepBase + base + c] = 1;
        }
      }
    }
    const swap = prev; prev = cur; cur = swap;
  }
  return { best: prev, used, size, row, n };
}

/** Replay a countKnapsack optimum: which player indices make up (k, c)? */
function knapsackPick(table, k, c) {
  const { used, size, row, n } = table;
  const chosen = [];
  let kk = k;
  let cc = c;
  for (let i = n; i >= 1 && kk > 0; i--) {
    if (used[i * size + kk * row + cc]) {
      chosen.push(i - 1);
      kk -= 1;
      cc -= table.prices[i - 1];
    }
  }
  return chosen;
}

/**
 * optimizePlan — the entry point.
 *
 * Slots that already hold a primary are left alone entirely; that primary's OWN
 * price comes off the budget first (its own, not the board's — the user may
 * have edited it, and their number is the one they intend to pay).
 *
 * @returns {{ok:true, assignments:Array, totalCost:number, totalValue:number}}
 *        | {{ok:false, reason:string}}
 */
export function optimizePlan(args, opts = {}) {
  const { plan, rosterSlots, pool, priceOf, valueOf, budget, budgetMode } = args || {};
  const buckets = (plan && plan.buckets) || {};
  const slots = rosterSlots || [];
  // Default OFF. Everything below that reads `bySlot` is dead in total mode.
  const bySlot = budgetMode === "slot";
  // A slot's explicit rail budget, or Infinity when it has none — "no earmark"
  // means "bound only by the total", never "bound to zero".
  const capOfSlot = (slot) => {
    if (!bySlot) return Infinity;
    const raw = Number(buckets[slot.id] && buckets[slot.id].budget);
    return Number.isFinite(raw) && raw > 0 ? Math.floor(raw) : Infinity;
  };

  // ── What is already spoken for ──────────────────────────────────────────
  const excluded = new Set();
  let committedCost = 0;
  const openSlots = [];
  slots.forEach((slot) => {
    const primary = primaryOf(buckets[slot.id]);
    if (primary) {
      excluded.add(primary.key);
      committedCost += Number(primary.price) || 0;
      return;
    }
    openSlots.push(slot);
  });

  const remaining = Math.floor((Number(budget) || 0) - committedCost);
  // 🔴 ok:false, NOT ok:true-with-nothing. A full plan is a legitimate state, but
  // returning success with zero assignments made the Optimize button a silent
  // no-op — the exact failure this feature's error path exists to prevent. The
  // chain in optimizePlans stops on !ok and hands `reason` back to the caller,
  // which is the surfacing path we already have. Do not "fix" this back to
  // ok:true without giving handleOptimize another way to see it.
  //
  // 🔴 The suggested next step must be reachable FROM THIS STATE — the rule that
  // got this message rewritten once already, when it pointed at "Optimize around
  // my commitments", which hits this same branch and is therefore a second dead
  // end. "Re-solve at my prices" is different: it opens every slot, so it is the
  // ONE mode that does something on a full plan. Now that it exists, it is the
  // right answer, and this is the commit where the rule changes the answer.
  if (openSlots.length === 0) {
    return {
      ok: false,
      reason: "Every slot is already committed — there is nothing left to fill. Use “Re-solve at my prices” to reconsider your commitments, or check the per-slot cost badges to see which are expensive.",
    };
  }
  if (remaining < openSlots.length) {
    return {
      ok: false,
      reason: `$${remaining} left cannot fill ${openSlots.length} open ${openSlots.length === 1 ? "slot" : "slots"} — every slot needs at least $1.`,
    };
  }

  // ── The eligible pool, priced and valued once ───────────────────────────
  const seen = new Set();
  const cands = [];
  (pool || []).forEach((p) => {
    if (!p || !p.pos) return;
    const key = keyOf(p);
    if (excluded.has(key) || seen.has(key)) return;
    seen.add(key);
    cands.push({
      player: p,
      key,
      pos: p.pos,
      price: Math.max(1, Math.round(Number(priceOf(p)) || 0)),
      value: Number(valueOf(p)) || 0,
    });
  });

  // ── Slot taxonomy ───────────────────────────────────────────────────────
  const benchSlots = openSlots.filter((s) => s.pos === "BN");
  const flexSlots = openSlots.filter((s) => s.pos === "FLEX");
  const sflexSlots = openSlots.filter((s) => s.pos === "SFLEX");
  const fixedSlots = openSlots.filter(
    (s) => s.pos !== "BN" && s.pos !== "FLEX" && s.pos !== "SFLEX"
  );

  // Position caps, net of what the plan already holds. A committed primary
  // counts against its position's cap — the cap is on the FINAL roster, not on
  // what this run adds.
  const committedByPos = {};
  slots.forEach((slot) => {
    const primary = primaryOf(buckets[slot.id]);
    if (primary) committedByPos[primary.pos] = (committedByPos[primary.pos] || 0) + 1;
  });

  const fixedOpen = {};
  SOLVER_POSITIONS.forEach((P) => { fixedOpen[P] = 0; });
  for (const s of fixedSlots) {
    if (!(s.pos in fixedOpen)) {
      return { ok: false, reason: `Unknown slot position "${s.pos}".` };
    }
    fixedOpen[s.pos] += 1;
  }

  const A = flexSlots.length;   // FLEX  → RB/WR/TE
  const B = sflexSlots.length;  // SFLEX → QB/RB/WR/TE
  // The tightest earmark on any open seat of that kind, or Infinity when none
  // of them carries one. Read only by the fold-in below.
  const tightestCap = (seats) => seats.reduce((m, sl) => Math.min(m, capOfSlot(sl)), Infinity);
  const tightestFlexCap = tightestCap(flexSlots);
  const tightestSflexCap = tightestCap(sflexSlots);
  const flexOk = (P) => FLEX_ELIGIBLE.includes(P);
  const sflexOk = (P) => SFLEX_ELIGIBLE.includes(P);

  // 🔴 Reserve the bench BEFORE solving. Without this the starters solve
  // consumes the whole budget and the bench cannot be filled at all.
  const benchReserve = benchSlots.length;
  const starterBudget = remaining - benchReserve;
  const starterCount = fixedSlots.length + A + B;
  if (starterBudget < starterCount) {
    return {
      ok: false,
      reason: `$${remaining} left cannot cover ${starterCount} starter ${starterCount === 1 ? "slot" : "slots"} plus $1 for each of ${benchReserve} bench ${benchReserve === 1 ? "slot" : "slots"}.`,
    };
  }

  const C = starterBudget;

  // ── Stage 1: per-position knapsack ──────────────────────────────────────
  const capLeft = {};
  SOLVER_POSITIONS.forEach((P) => {
    const raw = positionCap(P, slots) - (committedByPos[P] || 0);
    // Floored at this position's own open slots: a QB slot must always be
    // fillable by a QB, even for a user who has already hoarded three of them
    // on the bench. The cap shapes discretionary depth, it never blocks a
    // required start.
    capLeft[P] = Math.max(fixedOpen[P], raw);
  });

  const tables = {};
  for (const P of SOLVER_POSITIONS) {
    const room = fixedOpen[P] + (flexOk(P) ? A : 0) + (sflexOk(P) ? B : 0);
    const maxK = Math.min(room, capLeft[P]);
    if (maxK === 0) { tables[P] = null; continue; }
    let list = cands.filter((c) => c.pos === P);
    if (list.length < fixedOpen[P]) {
      return { ok: false, reason: `Not enough ${P} players available to fill ${fixedOpen[P]} open ${P} ${fixedOpen[P] === 1 ? "slot" : "slots"}.` };
    }
    // 🔴 THE SORT IS THE PRECONDITION FOR THE CAP GUARD, and it is applied ONLY
    // in slot mode. Reordering the candidate list can change which of two
    // equal-value optima the DP returns, so total mode keeps the pool order it
    // has always had and stays byte-identical.
    let capsAsc;
    if (bySlot) {
      list = list.slice().sort((a, b) => a.price - b.price || b.value - a.value);
      // The slots this position will fill, caps ascending: its own open slots,
      // then Infinity for every FLEX/SFLEX seat it might claim. Infinity sorts
      // last, so the vector is simply the fixed caps followed by padding — see
      // the flex matching after assignment for how those seats are really
      // constrained.
      const ownCapped = fixedSlots.filter((sl) => sl.pos === P)
        .map((sl) => ({ slot: sl, cap: capOfSlot(sl) }))
        .sort((a, b) => a.cap - b.cap);

      // 🔴 NAME THE SLOT BEFORE THE DP CAN SWALLOW IT. An unmeetable cap makes
      // this position's table empty, and stage 2 then fails with the generic
      // "no combination fills every starting slot" — true, useless, and silent
      // about the one number the user can change. Hall's condition on nested
      // eligibility is a counting test: the i-th tightest cap needs at least i
      // candidates at or under it. `list` is price-ascending, so the count is a
      // prefix walk.
      for (let i = 0; i < ownCapped.length; i++) {
        const { slot: sl, cap } = ownCapped[i];
        let fit = 0;
        for (const c of list) { if (c.price > cap) break; fit += 1; }
        if (fit >= i + 1) continue;
        const tail = "Raise that spot's budget, or switch back to your total budget.";
        return {
          ok: false,
          reason: fit === 0
            ? `${slotLabel(sl)} is capped at ${money(cap)} and no ${P} costs that little. ${tail}`
            : `${slotLabel(sl)} is capped at ${money(cap)}, and only ${fit} ${P}${fit === 1 ? "" : "s"} cost that little against ${i + 1} ${P} spots capped that tightly. ${tail}`,
        };
      }

      // 🔴 THE FOLD-IN, and it runs ONLY on the retry after a seat match
      // failed. A flex seat's cap is invisible to stage 1 because stage 2, not
      // stage 1, decides which position claims it, so the first solve pads
      // those claims with Infinity and picks the most expensive body it can
      // afford for a seat that may be capped at $40. Folding the tightest open
      // flex cap in as one extra entry per claim makes the DP choose a set that
      // the seat match can actually seat.
      //
      // Deliberately CONSERVATIVE in two ways. The tightest cap stands in for
      // every claim, so two flex seats capped $40 and $100 are both solved as
      // $40; and a merged vector shorter than maxK binds a position that ends
      // up claiming no flex seat at all. Both can only reject a solve that a
      // sharper bound would have allowed, never admit one the seat match would
      // then refuse, and this path is reached only when the unfolded solve has
      // already failed outright.
      const claimCaps = [];
      if (opts.foldFlexCaps) {
        if (flexOk(P)) for (let i = 0; i < A; i++) claimCaps.push(tightestFlexCap);
        if (sflexOk(P)) for (let i = 0; i < B; i++) claimCaps.push(tightestSflexCap);
      }
      // With the fold-in off this is `ownCapped` unchanged, which is what keeps
      // the first solve byte-identical to what it was.
      const mergedCaps = ownCapped.map((o) => o.cap).concat(claimCaps).sort((a, b) => a - b);
      capsAsc = new Array(maxK + 1).fill(Infinity);
      for (let k = 1; k <= maxK; k++) capsAsc[k] = k <= mergedCaps.length ? mergedCaps[k - 1] : Infinity;
    }
    const prices = new Int32Array(list.length);
    const values = new Float64Array(list.length);
    list.forEach((c, i) => { prices[i] = c.price; values[i] = c.value; });
    const t = countKnapsack(prices, values, maxK, C, capsAsc);
    t.prices = prices;
    t.list = list;
    t.maxK = maxK;
    tables[P] = t;
  }

  // ── Stage 2: combine positions over (cost, flex used, sflex used) ───────
  const row = C + 1;
  const sSize = row * (A + 1) * (B + 1);
  const sIdx = (c, a, b) => (c * (A + 1) + a) * (B + 1) + b;

  let dPrev = new Float64Array(sSize).fill(NEG);
  for (let c = 0; c <= C; c++) dPrev[sIdx(c, 0, 0)] = 0;

  // Parent trail, one layer per position: what (da, db, x) got us here.
  const trail = SOLVER_POSITIONS.map(() => ({
    da: new Uint8Array(sSize),
    db: new Uint8Array(sSize),
    x: new Int32Array(sSize).fill(-1),
  }));

  SOLVER_POSITIONS.forEach((P, pi) => {
    const t = tables[P];
    const dCur = new Float64Array(sSize).fill(NEG);
    const tr = trail[pi];
    const maxDa = t && flexOk(P) ? A : 0;
    const maxDb = t && sflexOk(P) ? B : 0;

    for (let c = 0; c <= C; c++) {
      for (let a = 0; a <= A; a++) {
        for (let b = 0; b <= B; b++) {
          const from = dPrev[sIdx(c, a, b)];
          if (from === NEG) continue;
          for (let da = 0; da <= maxDa && a + da <= A; da++) {
            for (let db = 0; db <= maxDb && b + db <= B; db++) {
              const k = fixedOpen[P] + da + db;
              if (!t) { // position takes no slots at all
                if (k === 0) {
                  const j = sIdx(c, a + da, b + db);
                  if (from > dCur[j]) { dCur[j] = from; tr.da[j] = da; tr.db[j] = db; tr.x[j] = 0; }
                }
                continue;
              }
              if (k > t.maxK) continue;
              const kBase = k * (C + 1);
              for (let x = 0; c + x <= C; x++) {
                const v = t.best[kBase + x];
                if (v === NEG) continue;
                const j = sIdx(c + x, a + da, b + db);
                const cand = from + v;
                if (cand > dCur[j]) {
                  dCur[j] = cand;
                  tr.da[j] = da;
                  tr.db[j] = db;
                  tr.x[j] = x;
                }
              }
            }
          }
        }
      }
    }
    dPrev = dCur;
  });

  const finalIdx = sIdx(C, A, B);
  if (dPrev[finalIdx] === NEG) {
    return { ok: false, reason: "No combination of available players fills every starting slot within the remaining budget." };
  }

  // ── Backtrack stage 2, then stage 1 ─────────────────────────────────────
  const perPosition = {};
  {
    let c = C, a = A, b = B;
    for (let pi = SOLVER_POSITIONS.length - 1; pi >= 0; pi--) {
      const P = SOLVER_POSITIONS[pi];
      const tr = trail[pi];
      const j = sIdx(c, a, b);
      const da = tr.da[j];
      const db = tr.db[j];
      const x = tr.x[j];
      const k = fixedOpen[P] + da + db;
      perPosition[P] = { k, x, da, db };
      c -= x;
      a -= da;
      b -= db;
    }
  }

  const assignments = [];
  let totalCost = 0;
  let totalValue = 0;
  const takenKeys = new Set();

  const flexQueue = [];   // extras destined for FLEX slots
  const sflexQueue = [];  // extras destined for SFLEX slots

  for (const P of SOLVER_POSITIONS) {
    const t = tables[P];
    const info = perPosition[P];
    if (!t || !info || info.k === 0) continue;
    const picks = knapsackPick(t, info.k, info.x)
      .map((i) => t.list[i])
      .sort((m, n2) => n2.value - m.value);

    if (picks.length !== info.k) {
      return { ok: false, reason: `Internal: could not reconstruct ${info.k} ${P} picks.` };
    }

    const ownSlots = fixedSlots.filter((s) => s.pos === P);

    // 🔴 IN SLOT MODE, WHICH PICK LANDS WHERE IS A MATCHING, NOT A RANKING.
    // Value is a property of the player, not of the slot he fills, so total
    // value and total cost are identical whichever legal assignment we choose
    // and only FEASIBILITY is at stake here.
    //
    // Caps ascending, and for each one take the MOST EXPENSIVE pick that still
    // fits. Feasible by the same exchange argument the guard rests on, and it
    // deliberately leaves the CHEAPEST picks over for the flex seats, which are
    // the ones still facing an unchecked cap below.
    if (bySlot && ownSlots.length) {
      const pool = picks.slice().sort((a, b) => a.price - b.price);
      const ordered = ownSlots.slice().sort((a, b) => capOfSlot(a) - capOfSlot(b));
      const leftovers = [];
      for (const slot of ordered) {
        const cap = capOfSlot(slot);
        let idx = -1;
        for (let i = pool.length - 1; i >= 0; i--) { if (pool[i].price <= cap) { idx = i; break; } }
        if (idx === -1) {
          return { ok: false, reason: `${slotLabel(slot)} is capped at ${money(cap)} and no ${P} in the plan's budget fits it. Raise that spot's budget, or switch back to your total budget.` };
        }
        const pick = pool.splice(idx, 1)[0];
        assignments.push({ slotId: slot.id, player: pick.player, price: pick.price, value: pick.value });
        takenKeys.add(pick.key);
        totalCost += pick.price;
        totalValue += pick.value;
      }
      leftovers.push(...pool);
      // Highest value first again, so the flex seats read the way they did.
      leftovers.sort((m, n2) => n2.value - m.value);
      for (let d = 0; d < info.da; d++) flexQueue.push(leftovers.shift());
      for (let d = 0; d < info.db; d++) sflexQueue.push(leftovers.shift());
      continue;
    }

    // Best go into the position's own slots; the extras are what the flex
    // slots claimed, which is why they were counted into k in the first place.
    let cursor = 0;
    for (const slot of ownSlots) {
      const pick = picks[cursor++];
      assignments.push({ slotId: slot.id, player: pick.player, price: pick.price, value: pick.value });
      takenKeys.add(pick.key);
      totalCost += pick.price;
      totalValue += pick.value;
    }
    for (let d = 0; d < info.da; d++) flexQueue.push(picks[cursor++]);
    for (let d = 0; d < info.db; d++) sflexQueue.push(picks[cursor++]);
  }

  // 🔴 THE FLEX SEATS ARE THE ONE PLACE THE SOLVER COULD NOT SEE THE CAP.
  // Stage 2 decides HOW MANY flex seats a position claims but never WHICH, so
  // those seats went into the cap vector as Infinity and are settled here
  // instead — over the UNION of every position's flex picks, cheapest pick to
  // tightest cap, which is the exact matching for nested eligibility.
  //
  // FLEX is matched before SFLEX and the two queues stay separate, so a QB —
  // which is SFLEX-eligible and not FLEX-eligible — can only ever land on an
  // SFLEX seat. Merging them would quietly make QBs flex-legal.
  const seatFail = (queue, seats) => {
    const picksAsc = queue.filter(Boolean).slice().sort((a, b) => a.price - b.price);
    const seatsAsc = seats.slice().sort((a, b) => capOfSlot(a) - capOfSlot(b));
    for (let i = 0; i < picksAsc.length && i < seatsAsc.length; i++) {
      const cap = capOfSlot(seatsAsc[i]);
      if (picksAsc[i].price > cap) {
        return { slot: seatsAsc[i], cap, pick: picksAsc[i] };
      }
    }
    return null;
  };
  const assignSeats = (queue, seats) => {
    if (!bySlot) {
      seats.forEach((slot, i) => {
        const pick = queue[i];
        if (!pick) return;
        assignments.push({ slotId: slot.id, player: pick.player, price: pick.price, value: pick.value });
        takenKeys.add(pick.key);
        totalCost += pick.price;
        totalValue += pick.value;
      });
      return null;
    }
    const bad = seatFail(queue, seats);
    if (bad) return bad;
    const picksAsc = queue.filter(Boolean).slice().sort((a, b) => a.price - b.price);
    const seatsAsc = seats.slice().sort((a, b) => capOfSlot(a) - capOfSlot(b));
    seatsAsc.forEach((slot, i) => {
      const pick = picksAsc[i];
      if (!pick) return;
      assignments.push({ slotId: slot.id, player: pick.player, price: pick.price, value: pick.value });
      takenKeys.add(pick.key);
      totalCost += pick.price;
      totalValue += pick.value;
    });
    return null;
  };

  // 🔴 A FAILED SEAT MATCH IS NOT AN ANSWER, IT IS A SOLVE THAT LOOKED
  // PAST A CAP. Stage 1 could not see the flex seats' earmarks, so it may hand
  // this matching a set nothing legal can be made of, at a seat a $40 rail
  // would have filled easily. Re-solve once with those caps folded in and take
  // that answer if it exists; the retry is the only place foldFlexCaps is ever
  // set, so it can run at most one level deep.
  //
  // The old message here read "the cheapest player left for it costs $60",
  // which described this solve's leftovers rather than the plan, and was
  // therefore false about the thing the user could check: there were 116 RBs
  // under $40 on the board while it said so.
  const onSeatFail = (bad) => {
    if (!opts.foldFlexCaps) {
      const folded = optimizePlan(args, { ...opts, foldFlexCaps: true });
      if (folded.ok) return folded;
    }
    const eligible = bad.slot.pos === "SFLEX" ? SFLEX_ELIGIBLE : FLEX_ELIGIBLE;
    return {
      ok: false,
      reason: `${slotLabel(bad.slot)} is capped at ${money(bad.cap)} and no ${orList(eligible)} fits it once your other spots are budgeted. Raise that spot's budget, or switch back to your total budget.`,
    };
  };

  const flexBad = assignSeats(flexQueue, flexSlots);
  if (flexBad) return onSeatFail(flexBad);
  const sflexBad = assignSeats(sflexQueue, sflexSlots);
  if (sflexBad) return onSeatFail(sflexBad);

  if (assignments.length !== starterCount) {
    return { ok: false, reason: "No combination of available players fills every starting slot within the remaining budget." };
  }

  // ── Bench: greedy, from whatever the starters did not spend ─────────────
  // Highest value first, always leaving $1 per still-empty bench slot. Greedy
  // is honest here: the objective does not score a bench, so there is no
  // optimum to miss — only "spend what is left on the best available depth".
  let benchBudget = remaining - totalCost;
  const benchPool = cands
    .filter((c) => !takenKeys.has(c.key))
    .sort((m, n2) => n2.value - m.value || m.price - n2.price);

  // 🔴 The caps bind here too, and this is where they were visibly needed: the
  // bench is the one place the objective has nothing to say, so left alone it
  // takes six backup QBs (PPG) or four kickers (VORP). Running the count across
  // committed primaries AND the starters just assigned keeps the cap a
  // whole-roster limit rather than a per-stage one.
  const posCount = { ...committedByPos };
  assignments.forEach((a) => { posCount[a.player.pos] = (posCount[a.player.pos] || 0) + 1; });
  const capOf = {};
  SOLVER_POSITIONS.forEach((P) => { capOf[P] = positionCap(P, slots); });
  const underCap = (c) => (posCount[c.pos] || 0) < (capOf[c.pos] === undefined ? Infinity : capOf[c.pos]);

  for (let i = 0; i < benchSlots.length; i++) {
    const slotsAfter = benchSlots.length - i - 1;
    const ceiling = benchBudget - slotsAfter;
    if (ceiling < 1) {
      return { ok: false, reason: `Ran out of budget filling the bench with $${benchBudget} left for ${benchSlots.length - i} slots.` };
    }
    // The bench was always price-aware through `ceiling`; a slot cap is just a
    // second ceiling on the same greedy step.
    const seatCap = capOfSlot(benchSlots[i]);
    const idx = benchPool.findIndex((c) => !takenKeys.has(c.key) && c.price <= ceiling && c.price <= seatCap && underCap(c));
    if (idx === -1) {
      if (bySlot && seatCap !== Infinity) {
        return { ok: false, reason: `${slotLabel(benchSlots[i])} is capped at ${money(seatCap)} and no player left fits it. Raise that spot's budget, or switch back to your total budget.` };
      }
      return { ok: false, reason: "No affordable players left to fill the bench within the position caps." };
    }
    const pick = benchPool[idx];
    benchPool.splice(idx, 1);
    takenKeys.add(pick.key);
    posCount[pick.pos] = (posCount[pick.pos] || 0) + 1;
    assignments.push({ slotId: benchSlots[i].id, player: pick.player, price: pick.price, value: pick.value });
    benchBudget -= pick.price;
    totalCost += pick.price;
    totalValue += pick.value;
  }

  return { ok: true, assignments, totalCost, totalValue };
}

/**
 * optimizePlans — one call, N complete alternative rosters.
 *
 * THE COLUMNS ARE PLANS NOW. Rank 0 is Plan A and rank N lands in cell N of
 * every slot, so reading DOWN a column is a whole draftable team and reading
 * ACROSS a row is your options at that slot.
 *
 *   Plan A — your commitments stay put; the solver fills what is open with the
 *            budget those commitments left behind.
 *   Plan B — every slot solved from scratch on the FULL budget, excluding every
 *            player Plan A uses.
 *   Plan C — the same, excluding A and B.  Plan D excludes A, B and C.
 *
 * 🔴 B, C AND D DELIBERATELY EXCLUDE YOUR COMMITTED PLAYERS, and this is the
 * one decision here somebody will want to revisit. A backup column exists
 * because you might not GET the guy — leaving Gibbs in Plan B makes Plan B not
 * a plan, just Plan A with different bench filler. The cost is that if you do
 * land every commitment, B/C/D are more pessimistic than reality.
 *
 * Plan A is constrained by your commitments and B–D are not, which is the right
 * asymmetry: A is your plan, B–D are what you do when it falls apart.
 *
 * 🔴 LOCKS ARE THE EXCEPTION TO THAT, in every rank. A locked slot is a player
 * you have already bought: B-D hold him at the price you paid rather than
 * re-solving his slot, because a column that spends his money again is a column
 * you cannot execute. See lockedPlanOf.
 *
 * Position caps and the caller's pool filtering apply to every rank
 * identically — each rank is a full, legal roster in its own right.
 *
 * `keepCommitments` makes EVERY rank honour your committed primaries instead of
 * only rank 0 — four alternatives that all still contain your guys, rather than
 * four answers to "what if I lose them".
 *
 * A rank that comes back ok:false STOPS the chain rather than failing the run:
 * three good columns beat an error message. The reason comes back so the caller
 * can say why the fourth is missing.
 *
 * @returns {{ranks: Array<{assignments,totalCost,totalValue}>, stoppedReason: string|null}}
 */
export function optimizePlans(args, { ranks = 4, keepCommitments = false } = {}) {
  const { plan, rosterSlots, pool } = args;
  const buckets = (plan && plan.buckets) || {};

  // Your committed primaries are "used" from the outset, so no later column can
  // hand you back a player you already have.
  const used = new Set();
  (rosterSlots || []).forEach((slot) => {
    const primary = primaryOf(buckets[slot.id]);
    if (primary) used.add(primary.key);
  });

  const out = [];
  let stoppedReason = null;

  for (let rank = 0; rank < ranks; rank++) {
    const available = (pool || []).filter((p) => !used.has(keyOf(p)));
    // keepCommitments: EVERY rank honours your committed primaries, so B-D are
    // alternatives that still contain your guys — the answer to "B-D read
    // pessimistic because they assume I lose everyone". Off by default, because
    // the default question a backup column answers is "what if I do not get
    // him", and that one needs him excluded.
    // 🔴 B-D drop your COMMITMENTS but never your LOCKS. A backup column exists
    // because you might not get the guy — but you already got the locked ones,
    // so a column that reassigns their slots is not a plan you could execute,
    // and one that hands their money back is a plan you cannot afford. Locked
    // slots are therefore held in every rank, at their locked price.
    const result = optimizePlan(rank === 0 || keepCommitments
      ? { ...args, pool: available }
      // From scratch: nothing held but the locks, whole remaining budget, only
      // what is left of the pool.
      : { ...args, plan: lockedPlanOf(plan), pool: available });

    if (!result.ok) { stoppedReason = result.reason; break; }
    result.assignments.forEach((a) => used.add(keyOf(a.player)));
    out.push(result);
  }

  return { ranks: out, stoppedReason };
}

export default optimizePlan;
